import { clsx } from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon } from '@/app/kit';
import type { IconName } from '@/app/module';
import { fill } from '@/copy';
import { uiCopy } from './copy';

/**
 * Sticker buttons: flat fill, ink outline, hard ink shadow; pressing pushes the button into its
 * own shadow (theme.css `tts-btn`). Every size is at least 44 px tall.
 *
 *   <Button variant="primary" size="lg" icon="ball" onClick={start}>Tip off</Button>
 *   <Button loading>Saving</Button>
 *   <IconButton icon="close" label="Close" onClick={close} />
 */
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'go' | 'accent';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

export const BUTTON_VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'tts-btn-primary',
  secondary: '',
  danger: 'tts-btn-danger',
  ghost: 'tts-btn-ghost',
  go: 'tts-btn-go',
  accent: 'tts-btn-accent',
};

export const BUTTON_SIZE_CLASS: Record<ButtonSize, string> = {
  sm: 'min-h-11 px-3 text-sm',
  md: '',
  lg: 'tts-btn-lg',
  xl: 'tts-btn-xl',
};
const VARIANT = BUTTON_VARIANT_CLASS;
const SIZE = BUTTON_SIZE_CLASS;

const ICON_PX: Record<ButtonSize, number> = { sm: 18, md: 22, lg: 24, xl: 28 };

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  /** Icon after the label instead of before it. */
  iconEnd?: IconName;
  /** Shows a spinner, sets aria-busy and blocks clicks. The label stays for width. */
  loading?: boolean;
  /** Read by screen readers while loading. */
  loadingLabel?: string;
  block?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  iconEnd,
  loading = false,
  loadingLabel = uiCopy.loading,
  block,
  className,
  children,
  disabled,
  onClick,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={loading ? (e) => e.preventDefault() : onClick}
      className={clsx('tts-btn relative', VARIANT[variant], SIZE[size], block && 'w-full', className)}
    >
      {loading ? (
        <>
          <span className="inline-flex animate-spin" aria-hidden="true">
            <Icon name="ball" size={ICON_PX[size]} fill="var(--color-peach)" />
          </span>
          <span className="sr-only">{loadingLabel}</span>
        </>
      ) : (
        icon && <Icon name={icon} size={ICON_PX[size]} />
      )}
      {children !== undefined && <span>{children}</span>}
      {iconEnd && !loading && <Icon name={iconEnd} size={ICON_PX[size]} />}
    </button>
  );
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: IconName;
  /** Required: the accessible name (there is no visible text). */
  label: string;
  variant?: ButtonVariant;
  /** 44, 48 or 56 px square. The glyph can be smaller, the target never is. */
  size?: 44 | 48 | 56;
  /** A small count bubble (the inbox bell). */
  badge?: number;
}

const SQUARE: Record<44 | 48 | 56, string> = { 44: 'size-11', 48: 'size-12', 56: 'size-14' };

export function IconButton({
  icon,
  label,
  variant = 'secondary',
  size = 48,
  badge,
  className,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={badge ? `${label}, ${fill(uiCopy.newCount, { n: badge })}` : label}
      title={label}
      {...rest}
      className={clsx('tts-btn relative min-h-0 p-0', VARIANT[variant], SQUARE[size], className)}
    >
      <Icon name={icon} size={size === 56 ? 28 : 22} />
      {badge ? (
        <span
          aria-hidden="true"
          className="absolute -top-2 -right-2 grid h-5 min-w-5 place-items-center rounded-chip border-2 border-ink bg-butter px-1 font-display text-xs font-black not-italic text-ink"
        >
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </button>
  );
}

/** Row of buttons with the gap the shadows need. */
export function ButtonRow({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx('flex flex-wrap items-center gap-3', className)}>{children}</div>;
}
